import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Typography,
  TextField,
  Button,
  Box,
  Paper,
  Divider,
  IconButton,
  Snackbar,
  Alert,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Fade,
  Zoom,
  Slide,
  Fab,
  Tooltip,
  Avatar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Rating,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Stack
} from '@mui/material';
import { Link } from 'react-router-dom';
import {
  Email,
  Phone,
  LocationOn,
  Facebook,
  Twitter,
  LinkedIn,
  Instagram,
  Send,
  Schedule,
  ExpandMore,
  WhatsApp,
  Telegram,
  YouTube,
  AccessTime,
  Business,
  Person,
  Message,
  Star,
  StarBorder,
  KeyboardArrowUp,
  ContactSupport,
  SupportAgent,
  Speed,
  Security
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';

const ContactUs = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    subject: '',
    category: '',
    message: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [rating, setRating] = useState(0);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [expandedAccordion, setExpandedAccordion] = useState(false);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Scroll to top functionality
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 400);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Form validation
  const validateForm = () => {
    const newErrors = {};
    
    if (!form.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!form.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!form.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(form.email)) {
      newErrors.email = 'Email is invalid';
    }
    if (!form.subject.trim()) newErrors.subject = 'Subject is required';
    if (!form.message.trim()) newErrors.message = 'Message is required';
    if (form.message.length < 10) newErrors.message = 'Message must be at least 10 characters';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      setSnackbar({
        open: true,
        message: 'Please fix the errors in the form',
        severity: 'error'
      });
      return;
    }

    setIsSubmitting(true);
    
    // Simulate API call
    try {
      await new Promise(resolve => setTimeout(resolve, 2000));
      
    setSnackbar({
      open: true,
        message: 'Your message has been sent successfully! We\'ll get back to you soon.',
      severity: 'success'
    });
      
      setForm({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        subject: '',
        category: '',
        message: ''
      });
      setRating(0);
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Failed to send message. Please try again.',
        severity: 'error'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: <Email sx={{ fontSize: 40, color: 'white' }} />,
      title: 'Email Us',
      value: 'info@ethioreview.com',
      subtitle: 'We\'ll respond within 24 hours',
      action: 'mailto:info@ethioreview.com',
      bgColor: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
    },
    {
      icon: <Phone sx={{ fontSize: 40, color: 'white' }} />,
      title: 'Call Us',
      value: '+251 911 123 456',
      subtitle: 'Mon-Fri, 9AM-6PM EAT',
      action: 'tel:+251911123456',
      bgColor: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)'
    },
    {
      icon: <LocationOn sx={{ fontSize: 40, color: 'white' }} />,
      title: 'Visit Us',
      value: 'Addis Ababa, Ethiopia',
      subtitle: 'Bole, Addis Ababa',
      action: 'https://maps.google.com',
      bgColor: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)'
    }
  ];

  const socialLinks = [
    { icon: <Facebook />, name: 'Facebook', color: '#1877F2', url: '#' },
    { icon: <Twitter />, name: 'Twitter', color: '#1DA1F2', url: '#' },
    { icon: <Instagram />, name: 'Instagram', color: '#E4405F', url: '#' },
    { icon: <LinkedIn />, name: 'LinkedIn', color: '#0A66C2', url: '#' },
    { icon: <YouTube />, name: 'YouTube', color: '#FF0000', url: '#' },
    { icon: <Telegram />, name: 'Telegram', color: '#0088CC', url: '#' }
  ];

  const faqData = [
    {
      question: 'How can I submit a news story?',
      answer: 'You can submit news stories through our contact form or email us directly at editor@ethioreview.com. Please include relevant details and sources.'
    },
    {
      question: 'What are your business hours?',
      answer: 'Our office is open Monday to Friday from 9:00 AM to 6:00 PM EAT. For urgent matters, you can reach us via phone or email.'
    },
    {
      question: 'How do I advertise on your platform?',
      answer: 'For advertising inquiries, please contact our advertising team at ads@ethioreview.com or call us directly.'
    },
    {
      question: 'Can I request a correction to an article?',
      answer: 'Yes, if you find any errors in our articles, please contact us with the specific details and we\'ll review and correct them promptly.'
    }
  ];

  return (
    <>
      <Box sx={{ minHeight: '100vh', pt: { xs: 8, md: 10 } }}>
      {/* Hero Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <Box
          sx={{
            background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
            color: 'white',
            py: { xs: 4, md: 6 },
            textAlign: 'center'
          }}
        >
          <Container maxWidth="lg">
      <Typography
              variant={isMobile ? 'h4' : 'h2'}
        fontWeight="bold"
        gutterBottom
              sx={{ mb: 2 }}
      >
              Get in Touch
      </Typography>
            <Typography
              variant={isMobile ? 'body1' : 'h6'}
              sx={{ opacity: 0.9, maxWidth: 600, mx: 'auto' }}
            >
              We'd love to hear from you. Send us a message and we'll respond as soon as possible.
            </Typography>
          </Container>
        </Box>
      </motion.div>

      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
                 {/* Contact Info Cards */}
         <motion.div
           initial={{ opacity: 0, y: 30 }}
           animate={{ opacity: 1, y: 0 }}
           transition={{ duration: 0.6, delay: 0.2 }}
         >
           <Box sx={{ display: 'flex', justifyContent: 'center', mb: 6 }}>
             <Grid container spacing={4} sx={{ maxWidth: 900 }}>
               {contactInfo.map((info, index) => (
                 <Grid xs={12} sm={6} md={4} key={index}>
                   <motion.div
                     whileHover={{ y: -8, scale: 1.02 }}
                     transition={{ duration: 0.3 }}
                   >
                     <Card
                       component="a"
                       href={info.action}
                       sx={{
                         p: 4,
                         textAlign: 'center',
                         textDecoration: 'none',
                         color: 'inherit',
                         minHeight: 280,
                         cursor: 'pointer',
                         transition: 'all 0.3s ease',
                         border: '2px solid',
                         borderColor: 'primary.main',
                         borderRadius: 3,
                         background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
                         boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                         display: 'flex',
                         flexDirection: 'column',
                         justifyContent: 'center',
                         '&:hover': {
                           boxShadow: theme.shadows[8],
                           transform: 'translateY(-4px)',
                           borderColor: 'primary.dark',
                           background: 'linear-gradient(145deg, #f8f9fa 0%, #ffffff 100%)',
                           borderWidth: '3px'
                         }
                       }}
                     >
                       <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%' }}>
                         <Avatar
                           sx={{
                             width: 90,
                             height: 90,
                             mb: 3,
                             background: info.bgColor,
                             color: 'white',
                             boxShadow: '0 8px 20px rgba(0,0,0,0.3)',
                             border: '4px solid',
                             borderColor: 'rgba(255,255,255,0.3)',
                             transition: 'all 0.3s ease',
                             '&:hover': {
                               transform: 'scale(1.1)',
                               boxShadow: '0 12px 28px rgba(0,0,0,0.4)'
                             }
                           }}
                         >
                           {info.icon}
                         </Avatar>
                         <Typography 
                           variant="h5" 
                           fontWeight="bold" 
                           sx={{ 
                             mb: 2,
                             fontSize: { xs: '1.5rem', sm: '1.75rem' },
                             lineHeight: 1.2
                           }}
                         >
                           {info.title}
                         </Typography>
                         <Typography 
                           variant="h6" 
                           color="primary" 
                           fontWeight="bold" 
                           sx={{ 
                             mb: 2,
                             fontSize: { xs: '1rem', sm: '1.125rem' },
                             wordBreak: 'break-word',
                             lineHeight: 1.3
                           }}
                         >
                           {info.value}
                         </Typography>
                         <Typography 
                           variant="body1" 
                           color="text.secondary"
                           sx={{
                             fontSize: { xs: '0.875rem', sm: '1rem' },
                             lineHeight: 1.4,
                             maxWidth: '100%',
                             wordBreak: 'break-word'
                           }}
                         >
                           {info.subtitle}
                         </Typography>
                       </Box>
                     </Card>
                   </motion.div>
                 </Grid>
               ))}
             </Grid>
           </Box>
         </motion.div>

        {/* Main Content */}
        <Grid container spacing={4}>
          {/* Contact Form */}
          <Grid xs={12} lg={7}>
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <Paper 
                elevation={4} 
                sx={{ 
                  p: { xs: 3, md: 4 }, 
                  borderRadius: 3,
                  background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
                  border: '1px solid',
                  borderColor: 'divider'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
                  <Avatar sx={{ bgcolor: 'primary.main', mr: 2, width: 50, height: 50 }}>
                    <Message />
                  </Avatar>
                  <Box>
                    <Typography variant="h5" fontWeight="bold">
                      Send us a Message
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      We'd love to hear from you. Fill out the form below and we'll get back to you soon.
                    </Typography>
                  </Box>
                </Box>

            <form onSubmit={handleSubmit}>
                  <Grid container spacing={3}>
                <Grid xs={12} sm={6}>
                  <TextField
                    label="First Name"
                    name="firstName"
                    fullWidth
                    required
                    value={form.firstName}
                    onChange={handleChange}
                        error={!!errors.firstName}
                        helperText={errors.firstName}
                        InputProps={{
                          startAdornment: <Person sx={{ mr: 1, color: 'text.secondary' }} />
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '&:hover fieldset': {
                              borderColor: 'primary.main',
                            },
                          },
                        }}
                  />
                </Grid>
                <Grid xs={12} sm={6}>
                  <TextField
                    label="Last Name"
                    name="lastName"
                    fullWidth
                    required
                    value={form.lastName}
                    onChange={handleChange}
                        error={!!errors.lastName}
                        helperText={errors.lastName}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '&:hover fieldset': {
                              borderColor: 'primary.main',
                            },
                          },
                        }}
                  />
                </Grid>
                    <Grid xs={12} sm={6}>
                  <TextField
                        label="Email Address"
                    name="email"
                    type="email"
                    fullWidth
                    required
                    value={form.email}
                    onChange={handleChange}
                        error={!!errors.email}
                        helperText={errors.email}
                        InputProps={{
                          startAdornment: <Email sx={{ mr: 1, color: 'text.secondary' }} />
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '&:hover fieldset': {
                              borderColor: 'primary.main',
                            },
                          },
                        }}
                  />
                </Grid>
                                         <Grid xs={12} sm={6}>
                  <TextField
                    label="Subject"
                    name="subject"
                    fullWidth
                    required
                    value={form.subject}
                    onChange={handleChange}
                         error={!!errors.subject}
                         helperText={errors.subject}
                         placeholder="What is this about?"
                         sx={{
                           '& .MuiOutlinedInput-root': {
                             '&:hover fieldset': {
                               borderColor: 'primary.main',
                             },
                           },
                         }}
                  />
                </Grid>
                     <Grid xs={12} sm={6}>
                       <FormControl fullWidth>
                         <InputLabel>Category</InputLabel>
                         <Select
                           name="category"
                           value={form.category}
                           label="Category"
                           onChange={handleChange}
                           sx={{
                             '& .MuiOutlinedInput-root': {
                               '&:hover fieldset': {
                                 borderColor: 'primary.main',
                               },
                             },
                           }}
                         >
                           <MenuItem value="general">General Inquiry</MenuItem>
                           <MenuItem value="news">News Submission</MenuItem>
                           <MenuItem value="advertising">Advertising</MenuItem>
                           <MenuItem value="technical">Technical Support</MenuItem>
                           <MenuItem value="feedback">Feedback</MenuItem>
                         </Select>
                       </FormControl>
                     </Grid>
                     
                     {/* Rating Section */}
                     <Grid xs={12}>
                       <Box sx={{ 
                         display: 'flex', 
                         alignItems: 'center', 
                         gap: 2, 
                         mb: 2,
                         p: 2,
                         bgcolor: 'grey.50',
                         borderRadius: 2,
                         border: '1px solid',
                         borderColor: 'divider'
                       }}>
                         <Typography variant="body2" fontWeight="medium">Rate your experience:</Typography>
                         <Rating
                           value={rating}
                           onChange={(event, newValue) => setRating(newValue)}
                           icon={<Star sx={{ color: 'primary.main' }} />}
                           emptyIcon={<StarBorder />}
                           size="large"
                         />
                       </Box>
                     </Grid>
                <Grid xs={12}>
                  <TextField
                         label="Your Message"
                    name="message"
                    fullWidth
                    required
                    multiline
                         rows={6}
                    value={form.message}
                    onChange={handleChange}
                         error={!!errors.message}
                         helperText={errors.message}
                         placeholder="Tell us how we can help you. Please provide as much detail as possible..."
                         sx={{
                           '& .MuiOutlinedInput-root': {
                             '&:hover fieldset': {
                               borderColor: 'primary.main',
                             },
                           },
                         }}
                  />
                </Grid>
                <Grid xs={12}>
                      <Button
                        type="submit"
                        variant="contained"
                        fullWidth
                        size="large"
                        disabled={isSubmitting}
                        startIcon={isSubmitting ? <CircularProgress size={20} /> : <Send />}
                        sx={{
                          py: 2,
                          fontSize: '1.1rem',
                          fontWeight: 'bold',
                          background: 'linear-gradient(45deg, #1e3c72 30%, #2a5298 90%)',
                          borderRadius: 2,
                          '&:hover': {
                            background: 'linear-gradient(45deg, #2a5298 30%, #1e3c72 90%)',
                            transform: 'translateY(-2px)',
                            boxShadow: '0 8px 16px rgba(0,0,0,0.2)'
                          },
                          '&:disabled': {
                            background: 'grey.400'
                          }
                        }}
                      >
                        {isSubmitting ? 'Sending Message...' : 'Send Message'}
                  </Button>
                </Grid>
              </Grid>
            </form>
          </Paper>
            </motion.div>
          </Grid>

          {/* Sidebar */}
          <Grid xs={12} lg={5}>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
            >
          
            </motion.div>
          </Grid>
        </Grid>

        {/* FAQ Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.8 }}
        >
          <Box sx={{ mt: 8 }}>
            <Typography variant="h4" fontWeight="bold" textAlign="center" gutterBottom>
              Frequently Asked Questions
            </Typography>
            <Typography variant="body1" color="text.secondary" textAlign="center" sx={{ mb: 4 }}>
              Find answers to common questions about our services
            </Typography>
            
            <Grid container spacing={2}>
              {faqData.map((faq, index) => (
                <Grid xs={12} md={6} key={index}>
                  <Accordion
                    expanded={expandedAccordion === index}
                    onChange={() => setExpandedAccordion(expandedAccordion === index ? false : index)}
                    sx={{
                      '&:before': { display: 'none' },
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      borderRadius: 2,
                      mb: 1
                    }}
                  >
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {faq.question}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography variant="body2" color="text.secondary">
                        {faq.answer}
            </Typography>
                    </AccordionDetails>
                  </Accordion>
        </Grid>
              ))}
      </Grid>
          </Box>
        </motion.div>
      </Container>

      {/* Scroll to Top Button */}
      <AnimatePresence>
        {showScrollTop && (
          <motion.div
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0 }}
            style={{
              position: 'fixed',
              bottom: 20,
              right: 20,
              zIndex: 1000
            }}
          >
            <Tooltip title="Scroll to top">
              <Fab
                color="primary"
                onClick={scrollToTop}
                sx={{
                  background: 'linear-gradient(45deg, #1e3c72 30%, #2a5298 90%)',
                  '&:hover': {
                    background: 'linear-gradient(45deg, #2a5298 30%, #1e3c72 90%)'
                  }
                }}
              >
                <KeyboardArrowUp />
              </Fab>
            </Tooltip>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
        {/* Footer */}
        <Box
        component="footer"
        sx={{
          py: 4,
          px: 2,
          backgroundColor: "background.paper",
          borderTop: 1,
          borderColor: "divider",
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            {/* Quick Links */}
            <Grid xs={12} sm={6} md={3}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold" }}>
                Quick Links
              </Typography>
                             <Stack spacing={1}>
                 {[
                   { name: "Home", path: "/" },
                   { name: "About Us", path: "/about" },
                   { name: "Contact", path: "/contact" }
                 ].map((item) => (
                   <motion.div whileHover={{ x: 5 }} key={item.name}>
                     <Link
                       to={item.path}
                       style={{
                         display: "block",
                         color: "inherit",
                         textDecoration: "none",
                       }}
                       sx={{
                         color: "text.secondary",
                         "&:hover": { color: "primary.main" },
                       }}
                     >
                       {item.name}
                     </Link>
                   </motion.div>
                 ))}
              </Stack>
            </Grid>

            {/* Categories */}
            <Grid xs={12} sm={6} md={3}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold" }}>
                Categories
              </Typography>
              <Stack spacing={1}>
                {["Politics", "Business", "Sports"].map((category) => (
                  <motion.div whileHover={{ x: 5 }} key={category}>
                    <Link
                      to={`/${category.toLowerCase()}-news`}
                      style={{
                        display: "block",
                        color: "inherit",
                        textDecoration: "none",
                      }}
                      sx={{
                        color: "text.secondary",
                        "&:hover": { color: "primary.main" },
                      }}
                    >
                      {category}
                    </Link>
                  </motion.div>
                ))}
              </Stack>
            </Grid>

            {/* Contact Info */}
            <Grid xs={12} sm={6} md={3}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold" }}>
                Contact Us
              </Typography>
              <Stack spacing={1.5}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <LocationOn color="primary" sx={{ mr: 1 }} />
                  <Typography variant="body2">
                    123 News Street, Addis Ababa, Ethiopia
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Phone color="primary" sx={{ mr: 1 }} />
                  <Typography variant="body2">+251 123 456 789</Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Email color="primary" sx={{ mr: 1 }} />
                  <Typography variant="body2">info@ethioreview.com</Typography>
                </Box>
              </Stack>
            </Grid>

            {/* Social Media & Newsletter */}
            <Grid xs={12} sm={6} md={3}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold" }}>
                Follow Us
              </Typography>
              <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
                {[
                  { icon: <Facebook />, color: "#3b5998", name: "Facebook" },
                  { icon: <Twitter />, color: "#1DA1F2", name: "Twitter" },
                  { icon: <Instagram />, color: "#E1306C", name: "Instagram" },
                  { icon: <YouTube />, color: "#FF0000", name: "YouTube" },
                  { icon: <LinkedIn />, color: "#0077B5", name: "LinkedIn" },
                ].map((social) => (
                  <motion.div whileHover={{ scale: 1.1 }} key={social.name}>
                    <IconButton
                      href="#"
                      target="_blank"
                      rel="noopener"
                      sx={{
                        backgroundColor: `${social.color}10`,
                        color: social.color,
                        "&:hover": { backgroundColor: `${social.color}20` },
                      }}
                    >
                      {social.icon}
                    </IconButton>
                  </motion.div>
                ))}
              </Stack>

              <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold" }}>
                Newsletter
              </Typography>
              <Box component="form" sx={{ display: "flex" }}>
                <TextField
                  size="small"
                  placeholder="Your email"
                  variant="outlined"
                  fullWidth
                  sx={{ mr: 1 }}
                />
                <Button
                  variant="contained"
                  color="primary"
                  sx={{ whiteSpace: "nowrap" }}
                >
                  Subscribe
                </Button>
              </Box>
            </Grid>
          </Grid>

          {/* Copyright & Legal */}
          <Box
            sx={{
              mt: 4,
              pt: 3,
              borderTop: 1,
              borderColor: "divider",
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="body2" color="text.secondary">
              © {new Date().getFullYear()} EthioReview. All rights reserved.
            </Typography>

            <Stack direction="row" spacing={2} sx={{ mt: { xs: 2, sm: 0 } }}>
              {[
                { name: "Privacy Policy", path: "/privacy" },
                { name: "Terms of Service", path: "/terms" },
                { name: "Cookie Policy", path: "/cookies" }
              ].map((item) => (
                <Typography
                  key={item.name}
                  component={Link}
                  to={item.path}
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                    textDecoration: "none",
                    "&:hover": { color: "primary.main" },
                  }}
                >
                  {item.name}
                </Typography>
              ))}
            </Stack>
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default ContactUs;
